import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Pets as PetsIcon,
} from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

interface AnimalAddErrorData {
  resumo: string;
  url: string;
  data: null;
}

interface AnimalAddErrorViewProps {
  animalAddErrorData: AnimalAddErrorData;
}

const AnimalAddErrorView: React.FC<AnimalAddErrorViewProps> = ({ animalAddErrorData }) => {
  const { t } = useTranslation('animal');
  const { resumo } = animalAddErrorData;

  // Extrair informações do resumo
  const extractInfoFromSummary = (summary: string) => {
    // Exemplo: "Animais não encontrados na fazenda: mimosa. Verifique os brincos/nomes."
    const animalMatch = summary.match(/fazenda: ([^.]+)\./i);
    const animalName = animalMatch ? animalMatch[1].trim() : 'Animal';
    
    return {
      animalName,
    };
  };

  const { animalName } = extractInfoFromSummary(resumo);

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        minHeight: 0,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}
    >
      <Box sx={{ p: 4 }}>
        {/* Alert de Erro */}
        <Alert
          severity="error"
          icon={<ErrorIcon />}
          sx={{
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {t('errors.animal_not_found')}
          </Typography>
          <Typography variant="body2">
            {resumo}
          </Typography>
        </Alert>

        {/* Card Principal */}
        <Card
          elevation={2}
          sx={{
            borderRadius: 3,
            border: `1px solid ${DESIGN_TOKENS.colors.status.error[100]}`,
            mb: 3,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar
                sx={{
                  bgcolor: DESIGN_TOKENS.colors.status.error[100],
                  color: DESIGN_TOKENS.colors.status.error[600],
                  width: 56,
                  height: 56,
                }}
              >
                <PetsIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.text.primary }}>
                  {t('errors.animal_not_found_description', { animalName })}
                </Typography>
                <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                  {t('errors.animal_not_found_subtitle')}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: DESIGN_TOKENS.colors.text.primary }}>
                {t('errors.possible_solutions')}
              </Typography>
              <Box sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto' }}>
                <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 1 }}>
                  • {t('errors.solution_check_name')}
                </Typography>
                <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 1 }}>
                  • {t('errors.solution_check_registration')}
                </Typography>
                <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 1 }}>
                  • {t('errors.solution_use_tag')}
                </Typography>
                <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                  • {t('errors.solution_check_list')}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default AnimalAddErrorView;